/* eslint-disable react/require-default-props */
import {
  Typography as MuiTypography,
  TypographyProps as MuiTypographyProps,
  styled,
  TypographyTypeMap,
} from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { forwardRef } from 'react';

const TypographyStyled = styled(MuiTypography)(({ theme }) => ({
  '&.Typography-light': {
    fontWeight: 300,
  },
  '&.Typography-regular': {
    fontWeight: 400,
  },
  '&.Typography-medium': {
    fontWeight: 500,
  },
  '&.Typography-semibold': {
    fontWeight: 600,
  },
  '&.Typography-bold': {
    fontWeight: 700,
  },
  '&.Typography-body-l': {
    fontSize: '1.25rem',
    lineHeight: 1.6,
  },
  '&.Typography-body-m': {
    fontSize: '1.125rem',
    lineHeight: 1.32,
  },
  '&.Typography-body-s': {
    fontSize: '1rem',
    lineHeight: 1.48,
  },
  '&.Typography-body-xs': {
    fontSize: '0.875rem',
    lineHeight: 1.4,
  },
  '&.Typography-body-xxs': {
    fontSize: '0.75rem',
    fontWeight: 1.3,
  },
  '&.Typography-link': {
    padding: 'unset',
    background: 'unset',
    appearance: 'none',
    cursor: 'pointer',
    '&:hover, &:active, &:focus': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      background: 'unset',
    },
  },
}));

export type TypographyProps<
  T extends React.ElementType = TypographyTypeMap['defaultComponent']
> = Omit<MuiTypographyProps<T>, 'variant'> & {
  variant?:
    | MuiTypographyProps['variant']
    | `body-${'l' | 'm' | 's' | 'xs' | 'xxs'}`;
  fontWeight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold';
  link?: boolean;
  whiteSpace?: CSSProperties['whiteSpace'];
} & React.ComponentProps<T>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Typography = forwardRef<HTMLElement, TypographyProps<any>>(
  (
    {
      className = '',
      variant = 'body1',
      fontWeight = 'regular',
      whiteSpace,
      link = false,
      color,
      style,
      ...props
    },
    ref
  ) => (
    <TypographyStyled
      ref={ref}
      className={`${className} Typography-${variant} Typography-${fontWeight} ${
        link ? 'Typography-link' : ''
      }`}
      variant={variant.startsWith('body-') ? 'inherit' : variant}
      color={color || (link ? 'primary' : undefined)}
      style={{ whiteSpace, ...style }}
      {...props}
    />
  )
);

Typography.displayName = 'Typography';

export default Typography as <
  T extends React.ElementType = TypographyTypeMap['defaultComponent']
>(
  props: React.PropsWithRef<TypographyProps<T>>
) => React.JSX.Element;
